import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import {
  IoCalendarOutline,
  IoTimeOutline,
  IoPersonOutline,
  IoArrowBackOutline,
  IoCheckmarkCircleOutline,
  IoRepeatOutline,
  IoChevronDownOutline,
} from 'react-icons/io5'
import { motion, AnimatePresence } from 'framer-motion'
import {
  useCreateAppointment,
  useUpdateAppointment,
} from '../hooks/useAppointments'
import { usePatients } from '../hooks/usePatients'
import { LoadingSpinner } from '../components/ui/LoadingSpinner'
import { appointmentsService } from '../services/api/appointments'

const DEFAULT_START = '09:00'
const DEFAULT_END = '09:30'

interface RecurrenceRule {
  frequency: 'daily' | 'weekly' | 'monthly'
  interval: number
  endDate?: string
  count?: number
}

const combineDateTime = (date: string, time: string): string => {
  const [year, month, day] = date.split('-').map(Number)
  const [hours, minutes] = time.split(':').map(Number)
  const base = new Date(year!, month! - 1, day!, hours!, minutes!, 0, 0)
  return base.toISOString()
}

const splitDateTime = (iso: string): { date: string; time: string } => {
  const d = new Date(iso)
  const date = d.toISOString().split('T')[0]!
  const time = d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
  return { date, time }
}

export default function AddAppointment() {
  const navigate = useNavigate()
  const { id: editId } = useParams<{ id?: string }>()
  const isEdit = Boolean(editId)
  const query = new URLSearchParams(useLocation().search)
  const initialDate = query.get('date') ?? ''

  const [date, setDate] = useState(initialDate)
  const [startTime, setStartTime] = useState(DEFAULT_START)
  const [endTime, setEndTime] = useState(DEFAULT_END)
  const [patientId, setPatientId] = useState<number | ''>('')
  const [notes, setNotes] = useState('')
  const [loadingEdit, setLoadingEdit] = useState(isEdit)
  const [recurrenceEnabled, setRecurrenceEnabled] = useState(false)
  const [recurrence, setRecurrence] = useState<RecurrenceRule>({
    frequency: 'daily', interval: 1, endDate: '',
  })
  const [recurrenceMode, setRecurrenceMode] = useState<'endDate' | 'count'>('endDate')

  const { data: patientsData, isLoading: isLoadingPatients } = usePatients({ limit: 100 })
  const patients = useMemo(() => patientsData?.data ?? [], [patientsData])

  const createAppointment = useCreateAppointment()
  const updateAppointment = useUpdateAppointment()

  useEffect(() => {
    if (!isEdit || !editId) return
    appointmentsService.getById(Number(editId))
      .then(res => {
        if (!res.success || !res.data) return
        const appt = res.data
        const { date: d, time: st } = splitDateTime(appt.startDate)
        const { time: et } = splitDateTime(appt.endDate)
        setDate(d)
        setStartTime(st)
        setEndTime(et)
        setPatientId(appt.patientId)
        setNotes(appt.notes ?? '')
      })
      .finally(() => setLoadingEdit(false))
  }, [isEdit, editId])

  const selectedPatient = useMemo(() => {
    if (!patientId) return null
    return patients.find(p => p.id === patientId) ?? null
  }, [patientId, patients])

  const handleSubmit = async () => {
    if (!date) { toast.error('Veuillez choisir une date'); return }
    if (!startTime || !endTime) { toast.error("Veuillez renseigner l'heure de début et de fin"); return }
    if (startTime >= endTime) { toast.error("L'heure de début doit précéder l'heure de fin"); return }
    if (!patientId) { toast.error('Sélectionnez un patient'); return }

    try {
      const recurrencePayload: RecurrenceRule | undefined = recurrenceEnabled && !isEdit ? {
        frequency: recurrence.frequency,
        interval: recurrence.interval,
        ...(recurrenceMode === 'endDate' && recurrence.endDate ? { endDate: recurrence.endDate } : {}),
        ...(recurrenceMode === 'count' && recurrence.count ? { count: recurrence.count } : {}),
      } : undefined

      if (isEdit && editId) {
        await updateAppointment.mutateAsync({
          id: Number(editId),
          data: {
            patientId: patientId as number,
            startDate: combineDateTime(date, startTime),
            endDate: combineDateTime(date, endTime),
            notes: notes || undefined,
          },
        })
        toast.success('Rendez-vous modifié avec succès')
      } else {
        await createAppointment.mutateAsync({
          patientId: patientId as number,
          startDate: combineDateTime(date, startTime),
          endDate: combineDateTime(date, endTime),
          notes: notes || undefined,
          recurrence: recurrencePayload,
        })
        if (recurrenceEnabled) {
          toast.success('Série de rendez-vous créée avec succès')
        } else {
          toast.success('Rendez-vous créé avec succès')
        }
      }
      navigate('/planning')
    } catch (error) {
      const message = error instanceof Error ? error.message : "Une erreur s'est produite"
      toast.error(message)
    }
  }

  const formatDate = (dateString: string) => {
    if (!dateString) return ''
    const [year, month, day] = dateString.split('-').map(Number)
    return new Date(year!, month! - 1, day!).toLocaleDateString('fr-FR', {
      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
    })
  }

  if (loadingEdit) {
    return (
      <div className='flex items-center justify-center h-full'>
        <LoadingSpinner />
      </div>
    )
  }

  const isPending = createAppointment.isPending || updateAppointment.isPending

  return (
    <div className='min-h-full'>
      <div className='p-6 lg:p-8 max-w-5xl mx-auto'>
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className='mb-8'>
          <button
            onClick={() => navigate('/planning')}
            className='flex items-center space-x-2 text-gray-600 dark:text-gray-400 hover:text-gray-200 mb-4 transition-colors'
          >
            <IoArrowBackOutline className='text-xl' />
            <span>Retour au planning</span>
          </button>
          <h1 className='text-4xl md:text-5xl font-bold mb-3 bg-gradient-to-r from-primary-400 via-blue-400 to-green-400 bg-clip-text text-transparent'>
            {isEdit ? 'Modifier le rendez-vous' : 'Nouveau rendez-vous'}
          </h1>
          <p className='text-gray-600 dark:text-gray-400 text-lg'>
            {isEdit ? 'Modifiez les informations du rendez-vous' : 'Planifiez un nouveau rendez-vous avec un patient'}
          </p>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className='space-y-6'>
          {/* Date et Heure */}
          <div className='bg-white dark:bg-dark-50 rounded-2xl p-6 border border-gray-200 dark:border-gray-800 shadow-lg'>
            <div className='flex items-center space-x-3 mb-6'>
              <div className='bg-blue-500/10 p-2 rounded-lg'>
                <IoCalendarOutline className='text-blue-400 text-xl' />
              </div>
              <h2 className='text-xl font-bold text-gray-900 dark:text-gray-100'>Date et heure</h2>
            </div>

            <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
              <div>
                <label htmlFor='appointment-date' className='block text-sm font-medium text-gray-300 mb-2'>
                  Date <span className='text-red-400'>*</span>
                </label>
                <input
                  id='appointment-date'
                  type='date'
                  value={date}
                  onChange={e => setDate(e.target.value)}
                  className='input-field w-full'
                  required
                />
                {date && (
                  <p className='text-xs text-gray-600 dark:text-gray-400 mt-2'>{formatDate(date)}</p>
                )}
              </div>
              <div>
                <label htmlFor='start-time' className='block text-sm font-medium text-gray-300 mb-2'>
                  Heure de début <span className='text-red-400'>*</span>
                </label>
                <input
                  id='start-time'
                  type='time'
                  value={startTime}
                  onChange={e => setStartTime(e.target.value)}
                  className='input-field w-full'
                  required
                />
              </div>
              <div>
                <label htmlFor='end-time' className='block text-sm font-medium text-gray-300 mb-2'>
                  Heure de fin <span className='text-red-400'>*</span>
                </label>
                <input
                  id='end-time'
                  type='time'
                  value={endTime}
                  onChange={e => setEndTime(e.target.value)}
                  className='input-field w-full'
                  required
                />
              </div>
            </div>

            {startTime && endTime && startTime < endTime && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className='mt-4 bg-green-500/10 border border-green-500/20 rounded-lg p-3'>
                <div className='flex items-center space-x-2 text-green-400 text-sm'>
                  <IoTimeOutline />
                  <span>
                    Durée :{' '}
                    {(() => {
                      const [startH, startM] = startTime.split(':').map(Number)
                      const [endH, endM] = endTime.split(':').map(Number)
                      const duration = (endH! * 60 + endM!) - (startH! * 60 + startM!)
                      const hours = Math.floor(duration / 60)
                      const minutes = duration % 60
                      return hours > 0 ? `${hours}h${minutes > 0 ? minutes : ''}` : `${minutes}min`
                    })()}
                  </span>
                </div>
              </motion.div>
            )}
          </div>

          {/* Patient */}
          <div className='bg-white dark:bg-dark-50 rounded-2xl p-6 border border-gray-200 dark:border-gray-800 shadow-lg'>
            <div className='flex items-center space-x-3 mb-6'>
              <div className='bg-primary-500/10 p-2 rounded-lg'>
                <IoPersonOutline className='text-primary-400 text-xl' />
              </div>
              <h2 className='text-xl font-bold text-gray-900 dark:text-gray-100'>Patient</h2>
            </div>
            <div>
              <label htmlFor='patient' className='block text-sm font-medium text-gray-300 mb-2'>
                Sélectionner un patient <span className='text-red-400'>*</span>
              </label>
              <select
                id='patient'
                value={patientId}
                onChange={e => setPatientId(e.target.value ? Number(e.target.value) : '')}
                className='input-field w-full'
                disabled={isLoadingPatients}
              >
                <option value='' disabled>
                  {isLoadingPatients ? 'Chargement des patients...' : 'Sélectionnez un patient'}
                </option>
                {patients.map(patient => (
                  <option key={patient.id} value={patient.id}>
                    {patient.lastName.toUpperCase()} {patient.firstName} (ID: {patient.id})
                  </option>
                ))}
              </select>

              {selectedPatient && (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className='mt-4 bg-gray-50 dark:bg-dark-50 rounded-xl p-4 border border-gray-200 dark:border-gray-700/50'>
                  <div className='flex items-center space-x-3'>
                    <div className='flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-primary-500/20 to-blue-500/20 border border-primary-500/30'>
                      <IoPersonOutline className='text-2xl text-primary-400' />
                    </div>
                    <div className='flex-1'>
                      <p className='font-semibold text-gray-900 dark:text-gray-100'>
                        {selectedPatient.lastName.toUpperCase()} {selectedPatient.firstName}
                      </p>
                      <p className='text-sm text-gray-600 dark:text-gray-400'>
                        {selectedPatient.disease || 'Aucune pathologie'}
                      </p>
                    </div>
                  </div>
                </motion.div>
              )}
            </div>
          </div>

          {/* Récurrence (création seulement) */}
          {!isEdit && (
            <div className='bg-white dark:bg-dark-50 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-lg overflow-hidden'>
              <button
                type='button'
                onClick={() => setRecurrenceEnabled(v => !v)}
                className='w-full flex items-center justify-between gap-3 p-6 hover:bg-gray-50 dark:hover:bg-dark-100 transition-colors'>
                <div className='flex items-center gap-3'>
                  <div className={`p-2 rounded-lg transition-colors ${recurrenceEnabled ? 'bg-primary-500/10' : 'bg-gray-100 dark:bg-gray-800'}`}>
                    <IoRepeatOutline className={`text-xl ${recurrenceEnabled ? 'text-primary-500' : 'text-gray-400'}`} />
                  </div>
                  <div className='text-left'>
                    <h2 className='text-base font-bold text-gray-900 dark:text-gray-100'>Récurrence</h2>
                    <p className='text-xs text-gray-500 dark:text-gray-400 mt-0.5'>
                      {recurrenceEnabled ? 'Série activée' : 'Rendez-vous unique'}
                    </p>
                  </div>
                </div>
                <div className='flex items-center gap-2 flex-shrink-0'>
                  <div className={`w-11 h-6 rounded-full transition-colors relative ${recurrenceEnabled ? 'bg-primary-500' : 'bg-gray-200 dark:bg-gray-700'}`}>
                    <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${recurrenceEnabled ? 'translate-x-5' : ''}`} />
                  </div>
                  <IoChevronDownOutline className={`text-gray-400 transition-transform ${recurrenceEnabled ? 'rotate-180' : ''}`} />
                </div>
              </button>
              <AnimatePresence>
                {recurrenceEnabled && (
                  <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }}
                    className='overflow-hidden border-t border-gray-100 dark:border-gray-800'>
                    <div className='p-6 space-y-4'>
                      {/* Fréquence + intervalle */}
                      <div className='grid grid-cols-2 gap-4'>
                        <div>
                          <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'>Fréquence</label>
                          <select value={recurrence.frequency}
                            onChange={e => setRecurrence(r => ({ ...r, frequency: e.target.value as RecurrenceRule['frequency'] }))}
                            className='input-field w-full'>
                            <option value='daily'>Quotidienne</option>
                            <option value='weekly'>Hebdomadaire</option>
                            <option value='monthly'>Mensuelle</option>
                          </select>
                        </div>
                        <div>
                          <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'>
                            Tous les {recurrence.frequency === 'daily' ? 'jours' : recurrence.frequency === 'weekly' ? 'semaines' : 'mois'}
                          </label>
                          <input type='number' min={1} max={99} value={recurrence.interval}
                            onChange={e => setRecurrence(r => ({ ...r, interval: Math.max(1, parseInt(e.target.value) || 1) }))}
                            className='input-field w-full' />
                        </div>
                      </div>
                      {/* Mode fin */}
                      <div className='flex gap-2'>
                        {(['endDate', 'count'] as const).map(mode => (
                          <button key={mode} type='button'
                            onClick={() => setRecurrenceMode(mode)}
                            className={`flex-1 py-1.5 rounded-xl text-sm font-medium border transition-colors ${
                              recurrenceMode === mode
                                ? 'bg-primary-50 dark:bg-primary-500/10 border-primary-300 dark:border-primary-500/40 text-primary-700 dark:text-primary-400'
                                : 'bg-gray-50 dark:bg-dark-100 border-gray-200 dark:border-gray-700 text-gray-500 hover:text-gray-700'
                            }`}>
                            {mode === 'endDate' ? 'Jusqu\'au' : 'Nombre de fois'}
                          </button>
                        ))}
                      </div>
                      {recurrenceMode === 'endDate' ? (
                        <div>
                          <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'>Date de fin</label>
                          <input type='date' value={recurrence.endDate ?? ''}
                            min={date || undefined}
                            onChange={e => setRecurrence(r => ({ ...r, endDate: e.target.value }))}
                            className='input-field w-full' />
                        </div>
                      ) : (
                        <div>
                          <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'>Nombre de répétitions</label>
                          <input type='number' min={2} max={365} value={recurrence.count ?? 10}
                            onChange={e => setRecurrence(r => ({ ...r, count: Math.max(2, parseInt(e.target.value) || 10) }))}
                            className='input-field w-full' />
                        </div>
                      )}
                      {/* Preview */}
                      <div className='bg-primary-50 dark:bg-primary-500/10 border border-primary-200 dark:border-primary-500/30 rounded-xl px-4 py-3 text-xs text-primary-700 dark:text-primary-400'>
                        <strong>Aperçu :</strong> RDV {
                          recurrence.frequency === 'daily' ? `tous les ${recurrence.interval} jour${recurrence.interval > 1 ? 's' : ''}`
                            : recurrence.frequency === 'weekly' ? `toutes les ${recurrence.interval} semaine${recurrence.interval > 1 ? 's' : ''}`
                              : `tous les ${recurrence.interval} mois`
                        }{recurrenceMode === 'count' && recurrence.count ? `, ${recurrence.count} fois` : ''}{recurrenceMode === 'endDate' && recurrence.endDate ? ` jusqu'au ${new Date(recurrence.endDate).toLocaleDateString('fr-FR')}` : ''}
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}

          {/* Notes */}
          <div className='bg-white dark:bg-dark-50 rounded-2xl p-6 border border-gray-200 dark:border-gray-800 shadow-lg'>
            <h2 className='text-xl font-bold text-gray-900 dark:text-gray-100 mb-4'>Notes (optionnel)</h2>
            <textarea
              rows={3}
              value={notes}
              onChange={e => setNotes(e.target.value)}
              className='w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-dark-100 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500 transition-all text-sm resize-none'
              placeholder='Informations complémentaires sur le rendez-vous...'
            />
          </div>

          {/* Actions */}
          <div className='flex flex-col sm:flex-row gap-4 pt-4'>
            <button
              type='button'
              onClick={() => navigate('/planning')}
              className='btn-secondary flex-1 flex items-center justify-center gap-2 py-3'
            >
              <IoArrowBackOutline className='text-xl' />
              Annuler
            </button>
            <button
              type='button'
              onClick={() => { void handleSubmit() }}
              className='btn-primary flex-1 flex items-center justify-center gap-2 py-3'
              disabled={isPending}
            >
              {isPending ? (
                <><LoadingSpinner size='sm' />{isEdit ? 'Modification...' : 'Création...'}</>
              ) : (
                <><IoCheckmarkCircleOutline className='text-xl' />{isEdit ? 'Enregistrer les modifications' : 'Créer le rendez-vous'}</>
              )}
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  )
}
